/**
 * useShopOrders — заявки «Юни Лавки» для менеджера (DC). Источник правды —
 * /api/shop-orders (perm `clients`). Список pending + резолв (done/cancelled;
 * отмена возвращает коины клиенту на бэке).
 *
 * Backend:
 *  - GET /api/shop-orders?status=pending → { results: ShopOrder[] }
 *  - PUT /api/shop-orders/:id  { status, note } → { ok }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export interface ShopOrder {
  id: number;
  telegram_id: number;
  client_id: string | null;
  item_key: string;
  title: string;
  cost_uc: number;
  status: "pending" | "done" | "cancelled";
  client_name: string | null;
  note: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

export interface UseShopOrdersResult {
  orders: ShopOrder[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  resolve: (id: number, status: "done" | "cancelled", note?: string) => Promise<{ ok: boolean; error?: string }>;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useShopOrders(status: "pending" | "done" | "cancelled" | "all" = "pending"): UseShopOrdersResult {
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<{ results: ShopOrder[] }>(`/api/shop-orders?status=${status}`, { signal: ac.signal });
        if (ac.signal.aborted) return;
        setOrders(r.results ?? []);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger, status]);

  const resolve = useCallback(async (id: number, next: "done" | "cancelled", note?: string) => {
    try {
      await api<{ ok: boolean }>(`/api/shop-orders/${id}`, { method: "PUT", body: { status: next, note } });
      setOrders((prev) => prev.filter((o) => o.id !== id));
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, []);

  return { orders, loading, error, reload, resolve };
}
